// Every client site we look after.

import { useState } from 'react';
import { api } from '../api.ts';
import { ago, day, phones, tabletOnline } from '../format.ts';
import type { SiteSummary } from '../types.ts';
import { Chip, Link, Loading, Problem, Shell, useLoad } from '../ui.tsx';

type Kind = 'all' | 'live' | 'setup' | 'attention';

const needsAttention = (s: SiteSummary) =>
  s.subscription.kind === 'overdue' || s.subscription.kind === 'grace_ended' || (s.live && !tabletOnline(s.tabletLastSeen)) || s.switchedOffAt !== null;

function status(s: SiteSummary): [string, 'green' | 'amber' | 'red' | 'plain'] {
  if (s.switchedOffAt) return ['Switched off', 'red'];
  if (s.live) return ['Live', 'green'];
  if (s.waitingChange) return ['Waiting for approval on the ops tablet', 'amber'];
  return ['Setting up', 'amber'];
}

export function Sites() {
  const { data, error, loading } = useLoad(() => api<{ sites: SiteSummary[] }>('GET', '/sites'), []);
  const [filter, setFilter] = useState<Kind>('all');
  const sites = data?.sites ?? [];
  const counts = {
    all: sites.length,
    live: sites.filter((s) => s.live && !s.switchedOffAt).length,
    setup: sites.filter((s) => !s.live).length,
    attention: sites.filter(needsAttention).length,
  };
  const shown = sites.filter((s) => filter === 'all' || (filter === 'live' ? s.live && !s.switchedOffAt : filter === 'setup' ? !s.live : needsAttention(s)));
  const cols = { gridTemplateColumns: 'minmax(0, 1.6fr) 190px 170px 150px 120px minmax(0, 1.2fr) minmax(0, 1.2fr)' };

  return (
    <Shell>
      <div className="head">
        <div className="head-title">
          <h1>Sites</h1>
          <p className="muted">Every client site we look after.</p>
        </div>
        <Link to="/new" className="btn primary">
          New site
        </Link>
      </div>
      {error && <Problem text={error} />}
      {loading && !data && <Loading />}
      {data && (
        <>
          <div className="grid4">
            <div className="card">
              <span className="stat green">{counts.live}</span>
              <span className="muted">Live</span>
            </div>
            <div className="card">
              <span className="stat amber">{counts.setup}</span>
              <span className="muted">Being set up</span>
            </div>
            <div className="card">
              <span className="stat">{sites.filter((s) => s.waitingChange).length}</span>
              <span className="muted">Waiting for approval on the tablet</span>
            </div>
            <div className="card">
              <span className="stat red">{sites.filter((s) => s.subscription.kind === 'overdue' || s.subscription.kind === 'grace_ended').length}</span>
              <span className="muted">Renewal overdue</span>
            </div>
          </div>
          <div className="row wrap" role="group" aria-label="Show">
            {(
              [
                ['all', `All · ${counts.all}`],
                ['live', `Live · ${counts.live}`],
                ['setup', `Setting up · ${counts.setup}`],
                ['attention', `Needs attention · ${counts.attention}`],
              ] as [Kind, string][]
            ).map(([k, label]) => (
              <button key={k} className={filter === k ? 'pill on' : 'pill'} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {label}
              </button>
            ))}
          </div>
          <div className="table">
            <div className="tr th" style={cols}>
              <span>Site</span>
              <span>Status</span>
              <span>Ops tablet</span>
              <span>Phones</span>
              <span>Last test</span>
              <span>Subscription</span>
              <span>Waiting for approval</span>
            </div>
            {shown.length === 0 && <div className="empty">{sites.length === 0 ? 'No sites yet. Start with “New site”.' : 'No sites here.'}</div>}
            {shown.map((s) => {
              const [label, tone] = status(s);
              const overdue = s.subscription.kind === 'overdue' || s.subscription.kind === 'grace_ended';
              return (
                <Link key={s.siteId} to={`/sites/${s.siteId}`} className="tr" style={cols}>
                  <span className="stack" style={{ gap: 4 }}>
                    <span style={{ fontWeight: 500 }}>{s.siteName}</span>
                    <span className="faint small">{s.clientName}</span>
                  </span>
                  <span>
                    <Chip tone={tone}>{label}</Chip>
                  </span>
                  <span className="muted">{s.tabletLastSeen ? `${tabletOnline(s.tabletLastSeen) ? 'Online' : 'Last seen'} · ${ago(s.tabletLastSeen)}` : s.live ? 'Not seen yet' : 'Not added yet'}</span>
                  <span className="muted">{s.live ? phones(s.phonesIos, s.phonesAndroid) : '—'}</span>
                  <span className="muted">{s.lastTest ? day(s.lastTest) : s.live ? 'Not sent yet' : '—'}</span>
                  <span className={overdue ? 'red' : 'muted'} style={overdue ? { fontWeight: 500 } : undefined}>
                    {s.subscriptionText}
                  </span>
                  <span className={s.waitingChange ? 'amber' : 'muted'}>{s.waitingChange ? `Waiting: ${s.waitingSummary}` : s.live ? 'Nothing waiting' : '—'}</span>
                </Link>
              );
            })}
          </div>
          <p className="faint small">Phones are counted, never named.</p>
        </>
      )}
    </Shell>
  );
}
